import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Copy, Save } from "lucide-react"
import { useToast } from "@/components/ui/use-toast"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"

interface GeneralSettingsState {
  name: string
  creditLimit: boolean
}

const defaultSettings: GeneralSettingsState = {
  name: "engageaipro.com",
  creditLimit: false,
}

export function GeneralSettings() {
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [settings, setSettings] = useState<GeneralSettingsState>(() => {
    const savedSettings = localStorage.getItem("generalSettings")
    return savedSettings ? JSON.parse(savedSettings) : defaultSettings
  })

  const chatbotId = "lqXclr6C-inTbZ353whFD"
  const characterCount = 14191

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(chatbotId)
      toast({
        title: "Copied!",
        description: "Chatbot ID has been copied to clipboard",
      })
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      })
    }
  }

  const handleSave = () => {
    if (!settings.name.trim()) {
      toast({
        title: "Error",
        description: "Name cannot be empty",
        variant: "destructive",
      })
      return
    }

    setIsLoading(true)
    try {
      localStorage.setItem("generalSettings", JSON.stringify(settings))
      toast({
        title: "Success",
        description: "Settings saved successfully",
      })
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold mb-4">General</h2>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Chatbot ID</Label>
            <div className="flex gap-2">
              <Input value={chatbotId} readOnly className="font-mono bg-muted" />
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopyId}
                className="shrink-0"
              >
                <Copy className="h-4 w-4" />
                <span className="sr-only">Copy ID</span>
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Number of characters</Label>
            <Input value={characterCount.toLocaleString()} readOnly className="bg-muted" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={settings.name}
              onChange={(e) => setSettings(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter chatbot name"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="creditLimit">Credit Limit</Label>
              <div className="text-sm text-muted-foreground">
                Enable to set a maximum credit limit
              </div>
            </div>
            <Switch
              id="creditLimit"
              checked={settings.creditLimit}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, creditLimit: checked }))}
            />
          </div>
        </div>

        <div className="mt-6">
          <Button onClick={handleSave} disabled={isLoading}>
            <Save className="mr-2 h-4 w-4" />
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      <div className="pt-6">
        <div className="text-sm text-destructive mb-6">DANGER ZONE</div>
        <div className="space-y-4">
          <div className="border border-destructive/20 rounded-lg p-4">
            <h3 className="text-base font-semibold mb-2">Delete All Conversations</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Once you delete all your conversations, there is no going back. Please be certain.
              All the conversations on this chatbot will be deleted. This action is not reversible.
            </p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive">Delete All Conversations</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete All Conversations</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete all conversations
                    for this chatbot.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          <div className="border border-destructive/20 rounded-lg p-4">
            <h3 className="text-base font-semibold mb-2">Delete Chatbot</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Once you delete your chatbot, there is no going back. Please be certain.
              All your uploaded data will be deleted. This action is not reversible.
            </p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive">Delete Chatbot</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Chatbot</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete your chatbot
                    and all associated data.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>
    </div>
  )
}